import { ProductConfigName } from '@oy/types';
import { Key } from '@oy/types/src/Config';
import { GET_FRONTEND_CONFIG_URL, getFrontendConfigs } from 'src/clients';
import { decryptAESHex } from 'src/utilities/crypter';
import useSWR from 'swr';
import { useDomainEnv } from './useDomainEnv';

export const useFrontendConfigs = () => {
  const { currentDomainEnv } = useDomainEnv();
  const isDevDomainEnv = currentDomainEnv === 'staging';
  const { data } = useSWR(
    GET_FRONTEND_CONFIG_URL,
    () =>
      getFrontendConfigs().then(res => {
        const configs = res?.data?.data ?? [];
        if (isDevDomainEnv) {
          return configs;
        }
        const decryptedConfigs = configs.map((config: any) => {
          const decryptedValue = decryptAESHex(
            config.value,
            import.meta.env.VITE_ENCRYPT_SECRET_KEY,
          );
          return {
            ...config,
            value: decryptedValue,
          };
        });

        return decryptedConfigs;
      }),
    {
      revalidateOnMount: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const isIncludedInList = (key: string, list: string, separator = ',') => {
    if (list) {
      const separatedList = list.split(separator).map(str => str.trim());
      if (separatedList.length > 0) {
        return separatedList.includes(key);
      }
    }
    return false;
  };

  const getConfigAsBoolean = (productName: ProductConfigName, key: Key) => {
    if (!data) return false;

    const config = data.find(
      (item: { productName: string; key: string }) =>
        item.productName === productName && item.key === key,
    );

    return (config?.value ?? 'false') === 'true';
  };

  const getConfigAsString = (productName: ProductConfigName, key: Key) => {
    if (!data) return '';

    const config = data.find(
      (item: { productName: string; key: string }) =>
        item.productName === productName && item.key === key,
    );

    return config?.value ?? '';
  };

  const getConfigAsNumber = (productName: ProductConfigName, key: Key) => {
    if (!data) return 0;

    const config = data.find(
      (item: { productName: string; key: string }) =>
        item.productName === productName && item.key === key,
    );

    return Number(config?.value) ?? 0;
  };

  return {
    frontendConfigs: data ?? [],
    isIncludedInList,
    getConfigAsBoolean,
    getConfigAsString,
    getConfigAsNumber,
  };
};
